import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/auth';
import Auth from '../../utils/auth';
import Cropper from 'react-easy-crop';
import './EditProfile.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { UserContext } from '../../utils/UserContext';


const FormInput = ({ label, type, name, value, onChange, required, ...rest }) => (
  <div className="formGroup">
    <label htmlFor={name}>
      {label} {required && <span className="required">*</span>}
    </label>
    <div className="inputWithIcon">
      <input
        type={type}
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        {...rest}
      />
    </div>
  </div>
);

// Utility to get the cropped image as blob/base64
function getCroppedImg(imageSrc, crop, zoom, aspect = 1) {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.crossOrigin = 'anonymous';
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const scale = image.naturalWidth / image.width;
      const cropX = crop.x * scale;
      const cropY = crop.y * scale;
      const cropWidth = crop.width * scale;
      const cropHeight = crop.height * scale;
      canvas.width = cropWidth;
      canvas.height = cropHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight
      );
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          resolve(reader.result);
        };
      }, 'image/jpeg');
    };
    image.onerror = reject;
  });
}

const EditProfile = () => {
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [profileImage, setProfileImage] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [userImages, setUserImages] = useState([]);
  const [showImageSelector, setShowImageSelector] = useState(false);
  const fileInputRef = useRef(null);
  const [profileImageId, setProfileImageId] = useState(null);
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null); 
  const [confirmText, setConfirmText] = useState('');
  const { user, setUser } = useContext(UserContext);
  const workspaceId = user?.workspace_id;

  // New helper — uses useCallback to avoid recreating on every render
  const fetchUserData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/user/user`, {
        params: { id: user.id, workspaceId: workspaceId }
      });

      setProfileData({
        firstName: data.firstName || '',
        lastName : data.lastName  || '',
        email    : data.email     || '',
        phone    : data.phoneNumber || ''
      });
      setProfileImageId(data.profileImage?.id || null);
      setUserImages(data.images || []);
      setProfileImage(data.profileImage?.url || null);
      setDataLoaded(true);
    } catch (err) {
      console.error('Error loading user data', err);
      toast.error('Error loading user data');
    }
  }, [user.id, workspaceId]);

  useEffect(() => {
    if (user && workspaceId) fetchUserData();
  }, [user, workspaceId, fetchUserData]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData({
      ...profileData,
      [name]: value
    });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    const updatedPasswordData = {
      ...passwordData,
      [name]: value
    };
    setPasswordData(updatedPasswordData);
    if (
        (name === 'newPassword' || name === 'confirmPassword') &&
        updatedPasswordData.newPassword !== '' &&
        updatedPasswordData.confirmPassword !== ''
    ) {
        setPasswordMismatch(updatedPasswordData.newPassword !== updatedPasswordData.confirmPassword);
    } else {
        setPasswordMismatch(false);
    }
   };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        id: user.id,
        workspaceId,
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email,
        phoneNumber: profileData.phone,
        profileImageId
      };
      await axios.put(`${API_URL}/user/user`, payload);
      const updatedUser = {
        ...user,
        email: profileData.email,
        phone_number: profileData.phone
      };
      Auth.setUser(updatedUser);
      setUser(updatedUser);
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile', error);
      toast.error('Error updating profile');
    }
  };

  const handleAskDeleteImage = (img) => {
    setImageToDelete(img);     
    setConfirmText('');       
    setShowDeleteModal(true); 
  };

  const handleDeleteImage = async () => {
  if (!imageToDelete) return;

  try {
    await axios({
      method: 'delete',                       
      url: `${API_URL}/user/user-delete-image`,
      headers: { 'Content-Type': 'application/json' },
      data: {                                 
        imageId: imageToDelete.id,
        userId:  user.id,
        workspaceId
      }
    });

    setUserImages(prev => prev.filter(img => img.id !== imageToDelete.id));
    if (profileImageId === imageToDelete.id) {
      const updatedUser = {
        ...user,
        profileImageId: null,
        profileImage:   null
      };

      Auth.setUser(updatedUser);
      setUser(updatedUser);

      setProfileImage(null);
      setProfileImageId(null);
    }
    toast.success('Image deleted');
  } catch (err) {
    console.error('Error deleting image', err);
    toast.error('Error deleting image');
  } finally {
    setShowDeleteModal(false);
    setImageToDelete(null);
  }
};


  const updateProfileImage = useCallback(
  async (imageId) => {
    try {
      await axios.put(`${API_URL}/user/user`, {
        id: user.id,
        workspaceId,
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email.trim().toLowerCase(),
        phoneNumber: profileData.phone,
        profileImageId: imageId,
      });

      const img = userImages.find(img => img.id === imageId);
      const bustedUrl = `${img.url}?t=${Date.now()}`;

      const updatedUser = {
        ...user,
        profileImageId: imageId,
        profileImage: { url: bustedUrl }
      };

      Auth.setUser(updatedUser);
      setUser(updatedUser);

      toast.success('Profile image updated');
    } catch (err) {
      console.error('Error updating image', err);
      toast.error('Error updating profile picture');
    }
  },
  [user, userImages, profileData, workspaceId, setUser]
  );

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    const { currentPassword, newPassword, confirmPassword } = passwordData;

    if (newPassword !== confirmPassword) {
      toast.error('New password and confirmation do not match');
      return;
    }

    try {
      const payload = {
        id: user.id,
        workspaceId,
        oldPassword: currentPassword,
        newPassword
      };
      await axios.put(`${API_URL}/user/user`, payload);
      toast.success('Password updated successfully');
    } catch (error) {
      console.error('Error updating password', error);
      toast.error('Error updating password: ' + (error.response?.data?.message || error.message));
    }finally {
        setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
        });
        setPasswordMismatch(false);
    }
  };

  // When the user selects an image
  const handleImageChange = async (e) => {
    setShowImageSelector(false);
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2.5 * 1024 * 1024) {
      toast.warn('File size exceeds 2.5 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setOriginalImage(ev.target.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropAndUpload = async () => {
    if (!originalImage || !croppedAreaPixels) return;
    try {
      const croppedBase64 = await getCroppedImg(originalImage, croppedAreaPixels, zoom);
      setProfileImage(croppedBase64);
      setShowCropper(false);
      setShowImageSelector(false);
      const base64Data = croppedBase64.split(',')[1];
      const response = await axios.post(`${API_URL}/user/user-upload-image`, {
        userId: user.id,
        workspaceId,
        fileBuffer: base64Data,
        fileName: 'profile.jpg',
        mimeType: 'image/jpeg',
        setAsProfile: true
      });
      const imageData = response.data;
      if (imageData?.id && imageData?.url) {
      const bustedUrl = `${imageData.url}?t=${Date.now()}`;
      const updatedUser = {
        ...user,
        profileImageId: imageData.id,
        profileImage:   { url: bustedUrl }
      };
        Auth.setUser(updatedUser); 
        setUser(updatedUser);
        setProfileImageId(imageData.id);
        setProfileImage(bustedUrl);
      
        toast.success('Image uploaded successfully!');
      } else {
        toast.error('Unexpected server response when uploading image.');
      }
      fileInputRef.current.value = null;
    } catch (error) {
      console.error('Error uploading image', error);
      toast.error('Error uploading image');
      fileInputRef.current.value = null;
    }
  };

  const handleUploadOriginal = async () => {
    if (!originalImage) return;
    setProfileImage(originalImage);
    setShowCropper(false);
    setShowImageSelector(false);
    const base64Data = originalImage.split(',')[1];
    try {
      const response = await axios.post(`${API_URL}/user/user-upload-image`, {
        userId: user.id,
        workspaceId,
        fileBuffer: base64Data,
        fileName: 'profile.jpg',
        mimeType: 'image/jpeg',
        setAsProfile: true
      });
      if (response.data && response.data.image && response.data.image.id) {
        setProfileImageId(response.data.image.id);
      }
      toast.success('Image uploaded successfully. Click Update Profile to save.');
      await fetchUserData();
    } catch (error) {
      console.error('Error uploading image', error);
      toast.error('Error uploading image');
    }
  };

  const handleCameraClick = () => {
    setShowImageSelector(true);
  };

  // Select existing image: only update preview and id in state, do not send request
  const handleSelectExistingImage = async (img) => {
    setProfileImage(img.url);
    setProfileImageId(img.id);
    setShowImageSelector(false);

    await updateProfileImage(img.id);  
  };

  // Open file input to upload new image and close the image selector modal, then open cropper
  const handleUploadNewImage = () => {
    setShowImageSelector(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  };

  return (
    <div className="editProfileContainer">
      <div className="profilePanel">
        {/* Modal Cropper */}
        {showCropper && (
          <div className="cropperModal">
            <div className="cropperContent">
              <div className="cropperAreaWrapper">
                <Cropper
                  image={originalImage}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  cropShape="round"
                  showGrid={false}
                  objectFit="horizontal-cover"
                />
              </div>
              <div className="cropperZoomControl">
                <label htmlFor="zoomRange">Zoom</label>
                <input
                  id="zoomRange"
                  type="range"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={e => setZoom(Number(e.target.value))}
                />
              </div>
              <div className="cropperActions">
                <button className="btn" onClick={handleCropAndUpload}>Crop and upload</button>
                <button className="btn" onClick={handleUploadOriginal}>Upload without cropping</button>
                <button className="btn" onClick={() => {setShowCropper(false); fileInputRef.current.value = null;}}>Cancel</button>
              </div>
            </div>
          </div>
        )}
        {/* NEW: Modal to select profile image */}
        {showImageSelector && (
          <div className="cropperModal">
            <div className="cropperContent">
              <h3 className="imageSelectorHeading">Select a profile image</h3>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                {userImages && userImages.length > 0 ? (
                  userImages.map(img => (
                    <div
                      key={img.id}
                      className="imageThumb"
                      onClick={() => handleSelectExistingImage(img)}
                      title="Select this image"
                    >
                      {/* thumbnail */}
                      <img
                        src={img.url}
                        alt={`Profile image ${img.id}`}
                      />

                      {/* button X */}
                      <button
                        className="deleteBtn"
                        onClick={(e) => { e.stopPropagation(); handleAskDeleteImage(img); }}
                        title="Delete image"
                      >
                        ×
                      </button>
                    </div>
                  ))
                ) : (
                  <span>You have no saved images.</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" onClick={handleUploadNewImage}>Upload new image</button>
                <button className="btn" onClick={() => setShowImageSelector(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
        {/* Personal Data Section */}
        <div className="section">
          <h2>Personal Data</h2>
          {/* Profile Image Section */}
          <div className="profileImageContainer">
            <div className="profileImage">
              {!showCropper && (
                profileImage ? (
                  <img
                    src={profileImage}
                    alt="Profile"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                  />
                ) : (
                  <div className="defaultAvatar">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="7" r="5" />
                      <path d="M20 21v-2a7 7 0 0 0-14 0v2" />
                    </svg>
                  </div>
                )
              )}
              <div className="cameraIcon" onClick={handleCameraClick} title="Change profile image">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleImageChange}
                />
              </div>
            </div>
            <div className="profileImageText">
              <p>Profile Image</p>
              <p className="imageSizeText">The proposed size is 512×512 px no bigger than 2.5 MB</p>
            </div>
          </div>
          <form onSubmit={handleUpdateProfile}>
            <div className="formRow">
              <FormInput
                label="First Name"
                type="text"
                name="firstName"
                value={profileData.firstName}
                onChange={handleProfileChange}
                required
              />
              <FormInput
                label="Last Name"
                type="text"
                name="lastName"
                value={profileData.lastName}
                onChange={handleProfileChange}
                required
              />
            </div>
            <div className="formRow">
              <FormInput
                label="Email"
                type="email"
                name="email"
                value={profileData.email}
                onChange={handleProfileChange}
                required
              />
              <FormInput
                label="Phone"
                type="tel"
                name="phone"
                value={profileData.phone}
                onChange={handleProfileChange}
              />
            </div>
            <div className="btnContainer">
              <button type="submit" className="btn btnPrimary">Update Profile</button>
            </div>
          </form>
        </div>
        {/* Change Password Section */}
        <div className="section">
          <h2>Change Password</h2>
          <form onSubmit={handleUpdatePassword}>
            <FormInput
              label="Current Password"
              type="password"
              name="currentPassword"
              value={passwordData.currentPassword}
              onChange={handlePasswordChange}
              required
              placeholder="Current Password"
            />
            <FormInput
              label="New Password"
              type="password"
              name="newPassword"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              required
              placeholder="New Password"
            />
            <FormInput
              label="Confirm Password"
              type="password"
              name="confirmPassword"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              required
              placeholder="Confirm Password"
            />
            {passwordMismatch && (
            <p style={{ color: 'red', marginTop: '10px', marginBottom: '10px' }}>
                ⚠️ The passwords do not match.
            </p>
            )}
            <div className="btnContainer">
              <button type="submit" className="btn btnPrimary">Update Password</button>
            </div>
          </form>
        </div>
        {showDeleteModal && (
          <div className="cropperModal"> {/* reutilizamos tu overlay + estilos */}
            <div className="cropperContent" style={{ maxWidth: 600, textAlign: 'center' }}>
              <h3>Confirm deletion</h3>
              <p>Type <strong>DELETE</strong> to remove this image permanently.</p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type DELETE here"
                style={{ width: '100%', padding: 8, marginBottom: 16 }}
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button
                  className="btn"
                  disabled={confirmText !== 'DELETE'}
                  onClick={handleDeleteImage}
                >
                  Delete
                </button>
                <button
                  className="btn"
                  onClick={() => { setShowDeleteModal(false); setImageToDelete(null); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditProfile;
